'use client';

import { useSubsidiaryFilter } from '@/@subsidiary/context';
import { expandOrgSelectionForDraft, normalizeOrgSelection } from '@/@subsidiary/orgSelection';
import {
    Button,
    Checkbox,
    CheckboxGroup,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    Input,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Multi-select drawer for hierarchy users and for internals View-as'd into a
 * parent–subsidiary group. Draft state is local so checking boxes does not
 * refire list queries or prefs.
 */
export function OrgSelectionDrawer() {
    const {
        isVisible,
        isInternal,
        options,
        hierarchyOptions,
        hasHierarchySwitcher,
        selectedOrgIds,
        setSelected,
        setHierarchyFilter,
        isOrgDrawerOpen,
        closeOrgDrawer,
        notifyOrgSwitch,
        isLoading,
        masqueradeOrgId,
    } = useSubsidiaryFilter();

    const drawerOptions = isInternal ? hierarchyOptions : options;
    const showForInternal = isInternal && hasHierarchySwitcher && masqueradeOrgId != null;
    const showForHierarchyUser = !isInternal && options.length > 1;

    const [draft, setDraft] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const optionOrgIds = useMemo(() => drawerOptions.map((o) => o.orgId), [drawerOptions]);

    // Seed draft from context whenever the drawer opens.
    useEffect(() => {
        if (!isOrgDrawerOpen) return;
        setDraft(expandOrgSelectionForDraft(selectedOrgIds, optionOrgIds));
        setSearchTerm('');
    }, [isOrgDrawerOpen, selectedOrgIds, optionOrgIds]);

    const filteredOptions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return drawerOptions;
        return drawerOptions.filter(
            (o) =>
                o.orgName.toLowerCase().includes(term) ||
                (o.sfAccountId && o.sfAccountId.toLowerCase().includes(term)),
        );
    }, [drawerOptions, searchTerm]);

    const handleSelectAll = useCallback(() => {
        setDraft(optionOrgIds);
    }, [optionOrgIds]);

    const handleClearAll = useCallback(() => {
        setDraft([]);
    }, []);

    const handleApply = useCallback(() => {
        if (draft.length === 0) return;
        notifyOrgSwitch();
        const next = normalizeOrgSelection(draft, drawerOptions.length);
        if (isInternal) {
            setHierarchyFilter(next);
        } else {
            setSelected(next);
        }
        closeOrgDrawer();
    }, [
        closeOrgDrawer,
        draft,
        drawerOptions.length,
        isInternal,
        notifyOrgSwitch,
        setHierarchyFilter,
        setSelected,
    ]);

    const handleCancel = useCallback(() => {
        closeOrgDrawer();
    }, [closeOrgDrawer]);

    if (!isVisible) return null;
    // Hierarchy users with 2+ orgs, or internals View-as'd into a group.
    if (!showForInternal && !showForHierarchyUser && !isOrgDrawerOpen) return null;
    if (
        !showForInternal &&
        !showForHierarchyUser &&
        isOrgDrawerOpen &&
        drawerOptions.length <= 1 &&
        !isLoading
    ) {
        return null;
    }

    const selectedCount = draft.length;
    const totalCount = drawerOptions.length;

    return (
        <Drawer isOpen={isOrgDrawerOpen} onClose={handleCancel} placement="left" size="sm">
            <DrawerContent>
                <DrawerHeader className="flex flex-col gap-1 border-b border-default-200">
                    <span className="flex items-center gap-2 text-large font-semibold">
                        <Icon icon="lucide:building-2" width={20} />
                        Organizations
                    </span>
                    <span className="text-small font-normal text-default-500">
                        Choose which organizations to view. {totalCount} available.
                    </span>
                </DrawerHeader>
                <DrawerBody className="gap-4 py-4">
                    <Input
                        placeholder="Search by name or Salesforce ID..."
                        value={searchTerm}
                        onValueChange={setSearchTerm}
                        startContent={
                            <Icon icon="lucide:search" width={16} className="text-default-400" />
                        }
                        size="sm"
                        variant="bordered"
                        isClearable
                        onClear={() => setSearchTerm('')}
                    />
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-small text-default-500">
                            {selectedCount} of {totalCount} selected
                        </span>
                        <div className="flex gap-1">
                            <Button size="sm" variant="light" onPress={handleSelectAll}>
                                Select all
                            </Button>
                            <Button size="sm" variant="light" onPress={handleClearAll}>
                                Clear all
                            </Button>
                        </div>
                    </div>
                    {isLoading && drawerOptions.length === 0 ? (
                        <p className="py-8 text-center text-small text-default-400">
                            Loading organizations…
                        </p>
                    ) : filteredOptions.length === 0 ? (
                        <p className="py-8 text-center text-small text-default-400">
                            No organizations match &quot;{searchTerm}&quot;
                        </p>
                    ) : (
                        <CheckboxGroup
                            aria-label="Organizations"
                            value={draft.map(String)}
                            onValueChange={(keys) => {
                                setDraft(keys.map(Number).filter((n) => !Number.isNaN(n)));
                            }}
                            className="gap-2"
                        >
                            {filteredOptions.map((org) => (
                                <Checkbox
                                    key={org.orgId}
                                    value={String(org.orgId)}
                                    classNames={{
                                        base: 'max-w-full w-full rounded-medium border border-transparent hover:bg-default-100 px-2 py-1.5 m-0',
                                        label: 'w-full',
                                    }}
                                >
                                    <div className="flex flex-col">
                                        <span className="text-small font-medium">
                                            {org.orgName}
                                        </span>
                                        {org.sfAccountId ? (
                                            <span className="text-tiny text-default-400">
                                                {org.sfAccountId}
                                            </span>
                                        ) : null}
                                    </div>
                                </Checkbox>
                            ))}
                        </CheckboxGroup>
                    )}
                </DrawerBody>
                <DrawerFooter className="border-t border-default-200">
                    <Button variant="light" onPress={handleCancel}>
                        Cancel
                    </Button>
                    <Button color="secondary" onPress={handleApply} isDisabled={draft.length === 0}>
                        Apply
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
}
